<script lang="ts">
    import AddToWishlistIconButton from "@apps/wishlist/components/AddToWishlistIconButton.svelte";
    import WishlistTheme from "@apps/wishlist/components/WishlistTheme/WishlistTheme.svelte";
    import { SAVE_WISHLIST_MODAL_ACTION_TYPES } from "@apps/wishlist/constants/constants";
    import { SaveWishlistModalWidgetDefinition } from "@apps/wishlist/definitions";
    import { WishlistProductStatusesQuery } from "@apps/wishlist/queries/WishlistProductStatusesQuery";
    import { WishlistProductStatusesService } from "@apps/wishlist/services/WishlistProductStatusesService";
    import { WishlistsService } from "@apps/wishlist/services/WishlistsService";
    import WishlistsStorefrontValuesService from "@apps/wishlist/services/WishlistsStorefrontValuesService";
    import { saveWishlistModalToken } from "@apps/wishlist/tokens";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import { ProductVariantIdManagerInterface } from "@lib/ProductVariantIdManager/ProductVariantIdManagerInterface";
    import { AuthenticationInfoProviderInterface } from "@modules/authentication/AuthenticationInfoProvider/AuthenticationInfoProviderInterface";
    import { ROLE } from "@modules/authentication/constants";
    import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
    import { I18nModule } from "@modules/i18n/I18nModule";
    import ShopifyAnalyticsManager from "@modules/shopify_analytics/ShopifyAnalyticsManager";
    import { lastValueFrom, map, of, switchMap } from "rxjs";
    import { onMount } from "svelte";
    import { container } from "tsyringe";

    import { ModalManager } from "@components/Modal/ModalManager";

    export let wishlistProductStatusesService: WishlistProductStatusesService;
    export let wishlistProductStatusesQuery: WishlistProductStatusesQuery;
    export let modalManager: ModalManager;
    export let authenticationInfoProvider: AuthenticationInfoProviderInterface;
    export let wishlistsStorefrontValuesService: WishlistsStorefrontValuesService;
    export let wishlistsService: WishlistsService;
    export let dynamicImportManager: DynamicImportManager;
    export let productIdManager: ProductIdManagerInterface;
    export let productVariantIdManager: ProductVariantIdManagerInterface;
    export let shopifyAnalyticsManager: ShopifyAnalyticsManager | null;
    export let afterWidgetInject: () => void;
    export let wishlistCounterPosition: "left-top" | "right-top";

    const wishlistBranding$ =
        wishlistsStorefrontValuesService.selectWishlistsBranding();

    const settings$ =
        wishlistsStorefrontValuesService.selectWishlistsSettings();

    const productId$ = productIdManager.productId$;
    const variantId$ = productVariantIdManager.variantId$;

    const wishlistProductStatus$ = productId$.pipe(
        switchMap((productId) => {
            return productId
                ? wishlistProductStatusesQuery.selectEntity(productId)
                : of();
        })
    );

    const productInWishlistStatus$ = productId$.pipe(
        switchMap((productId) => {
            return productId
                ? wishlistProductStatusesQuery.selectProductInWishlistStatus(
                      productId
                  )
                : of();
        })
    );

    const loading$ = productId$.pipe(
        switchMap((productId) => {
            return productId
                ? wishlistProductStatusesQuery.selectProductInLoadingStatus(
                      productId
                  )
                : of();
        })
    );

    const isCustomer$ = authenticationInfoProvider
        .selectRole()
        .pipe(map((role) => role === ROLE.Customer));

    onMount(() => {
        const productIdSubscription = productId$.subscribe((productId) => {
            if (productId) {
                wishlistProductStatusesService.addProductIdForFetching(
                    productId
                );
            }
        });

        afterWidgetInject();

        return () => {
            productIdSubscription.unsubscribe();
        };
    });

    let handleToggle = () => {
        if ($productInWishlistStatus$ && $productId$) {
            lastValueFrom(
                wishlistsService.deleteItem({
                    productId: $productId$,
                    variantId: null,
                    wishlistId: null,
                })
            );
        } else {
            handleAddItem();
        }
    };

    const handleOpenSaveModal = async () => {
        await dynamicImportManager.fetch({
            definition: SaveWishlistModalWidgetDefinition,
        });
        // Ensure the modal widget initializes asynchronously before opening it.
        setTimeout(() => {
            modalManager.openModal(saveWishlistModalToken, undefined, {
                actionType: SAVE_WISHLIST_MODAL_ACTION_TYPES.addItem,
                productId: $productId$,
                variantId: $variantId$,
            });
        }, 0);
    };

    const handleAddItem = () => {
        if (!$settings$) return;

        if ($settings$.isDisabledForGuests && !$isCustomer$) {
            const email = wishlistsService.getGuestEmailFromLocalStorage();

            if (email) {
                if (!$productId$) return;
                lastValueFrom(
                    wishlistsService.saveGuestItem({
                        email,
                        productId: $productId$,
                        variantId: $variantId$ || null,
                    })
                );

                if (shopifyAnalyticsManager) {
                    shopifyAnalyticsManager.sendVisitorInfo({ email });
                }
            } else {
                handleOpenSaveModal();
            }
        } else if ($productId$) {
            lastValueFrom(
                wishlistsService.addItem({
                    productId: $productId$,
                    variantId: $variantId$,
                    wishlistId: null,
                })
            );
        }
    };

    const i18nModule = container.resolve(I18nModule);
    const add_to_wishlist_button_accessibility_label$ =
        i18nModule.exports.i18n.selectT(
            "wishlist.critical.AddToWishlistProductCardWidget",
            "add_to_wishlist_button_accessibility_label"
        );
    const remove_from_wishlist_button_accessibility_label$ =
        i18nModule.exports.i18n.selectT(
            "wishlist.critical.AddToWishlistProductCardWidget",
            "remove_from_wishlist_button_accessibility_label"
        );

    const button_accessibility_label$ = productInWishlistStatus$.pipe(
        switchMap((productInWishlistStatus) =>
            productInWishlistStatus
                ? remove_from_wishlist_button_accessibility_label$
                : add_to_wishlist_button_accessibility_label$
        )
    );
</script>

{#if $wishlistBranding$}
    <WishlistTheme branding={$wishlistBranding$}>
        <AddToWishlistIconButton
            productInWishlistStatus={$productInWishlistStatus$}
            loading={$loading$}
            button_accessibility_label={$button_accessibility_label$}
            isCounterEnabled={$wishlistBranding$.wishlistButtonSettings
                .isCounterEnabled}
            wishlistProductStatus={$wishlistProductStatus$}
            {wishlistCounterPosition}
            wishlistBrandingIconSettings={$wishlistBranding$.wishlistIconSettings}
            on:toggle={handleToggle}
        />
    </WishlistTheme>
{/if}
